import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Image, 
  Calendar, 
  MessageSquare, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  FileText,
  User,
  Ticket
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLayout = ({ children }) => {
  const { user, logout, getAuthHeaders } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/contact/unread-count`, {
        headers: getAuthHeaders()
      });
      setUnreadMessages(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/admin/content', icon: FileText, label: 'Contenu du site' },
    { path: '/admin/creations', icon: Image, label: 'Créations' },
    { path: '/admin/events', icon: Calendar, label: 'Événements & Workshops' },
    { path: '/admin/bookings', icon: Ticket, label: 'Réservations' },
    { path: '/admin/messages', icon: MessageSquare, label: 'Messages', badge: unreadMessages },
    { path: '/admin/settings', icon: User, label: 'Mon compte' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-sm z-40 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 text-[#8B4B7C]"
          >
            <Menu size={24} />
          </button>
          <img 
            src="https://customer-assets.emergentagent.com/job_tevaite-creations/artifacts/btmr9mu6_image.png"
            alt="Tevaite"
            className="h-10 w-auto"
          />
          <div className="w-10" />
        </div>
      </header>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 lg:translate-x-0 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#E8A5C4]/20">
            <div className="flex items-center justify-between">
              <img 
                src="https://customer-assets.emergentagent.com/job_tevaite-creations/artifacts/btmr9mu6_image.png"
                alt="Tevaite"
                className="h-12 w-auto"
              />
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="lg:hidden p-2 text-[#8B4B7C]"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-[#8B4B7C] text-white'
                    : 'text-[#6B5A62] hover:bg-[#E8A5C4]/20'
                }`}
              >
                <item.icon size={20} />
                <span className="font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <span className={`ml-auto px-2 py-0.5 text-xs rounded-full ${
                    isActive(item.path) ? 'bg-white text-[#8B4B7C]' : 'bg-[#C45B9F] text-white'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-[#E8A5C4]/20">
            {/* View Site Button */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 w-full px-4 py-3 mb-3 bg-[#8B4B7C] text-white rounded-xl hover:bg-[#C45B9F] transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              <span className="font-medium">Voir le site</span>
            </a>
            
            <div className="px-4 py-3 mb-2">
              <p className="text-sm text-[#6B5A62]">Connecté en tant que</p>
              <p className="font-medium text-[#3D2E35] truncate">{user?.name}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-[#6B5A62] hover:bg-red-50 hover:text-red-600 transition-all duration-200"
            >
              <LogOut size={20} />
              <span className="font-medium">Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
